<!--
  OrderDailyStatView.vue — 每日订单
  展示每日订单签约、扣款统计数据，按渠道维度（总数/快钱/合利宝/京东）分组。
  功能：日期范围筛选、分页查询、CSV 下载。
  接口：GET /balance/order/stats/daily/order-stat
-->
<template>
  <div>
    <!-- 搜索栏 -->
    <a-card
      :bordered="false"
      style="margin-bottom: 16px"
    >
      <a-form
        layout="inline"
        :model="searchForm"
      >
        <a-form-item label="日期范围">
          <a-range-picker
            v-model:value="dateRange"
            value-format="YYYY-MM-DD"
            :allow-clear="true"
          />
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button
              type="primary"
              :loading="loading"
              @click="handleSearch"
            >查询</a-button>
            <a-button @click="handleReset">重置</a-button>
            <a-button
              :loading="downloading"
              @click="handleDownload"
            >下载</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- 折线图 -->
    <a-card
      :bordered="false"
      style="margin-bottom: 16px"
    >
      <template #title>
        每日订单趋势
        <a-radio-group
          v-model:value="chartDimension"
          size="small"
          style="margin-left: 12px"
        >
          <a-radio-button value="Total">总数</a-radio-button>
          <a-radio-button value="Kq">快钱</a-radio-button>
          <a-radio-button value="Hlb">合利宝</a-radio-button>
          <a-radio-button value="Jd">京东</a-radio-button>
        </a-radio-group>
      </template>
      <a-spin :spinning="loading">
        <v-chart
          :option="chartOption"
          autoresize
          style="height: 320px"
        />
      </a-spin>
    </a-card>

    <!-- 数据表格 -->
    <a-card
      :bordered="false"
      title="每日订单"
    >
      <a-table
        :columns="columns"
        :data-source="tableData"
        :loading="loading"
        :pagination="pagination"
        row-key="statDate"
        @change="handleTableChange"
        :scroll="{ x: 'max-content' }"
        size="small"
        bordered
      />
    </a-card>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { message } from "ant-design-vue";
import VChart from "vue-echarts";
import { analyticsUrl } from "@/utils/api";
import { get } from "@/utils/request";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
} from "echarts/components";

use([
  CanvasRenderer,
  LineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
]);

const loading = ref(false);
const downloading = ref(false);
const dateRange = ref([]);
const tableData = ref([]);
const chartDimension = ref("Total");

const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (total) => `共 ${total} 条`,
});

const searchForm = reactive({
  startDate: "",
  endDate: "",
});

const nowrap = { style: { whiteSpace: "nowrap" } };

// ========================
// 折线图
// ========================

const chartOption = computed(() => {
  const suffix = chartDimension.value;
  const rows = [...tableData.value].reverse();
  const dates = rows.map((r) => r.statDate.slice(5));
  return {
    tooltip: { trigger: "axis" },
    legend: {
      data: [
        "新签约",
        "要扣款",
        "已扣款",
        "首次扣款",
        "非首次扣款",
        "留存签约",
      ],
      selected: { 留存签约: false },
      bottom: 0,
    },
    grid: { top: 10, left: 0, right: 8, bottom: 36, containLabel: true },
    xAxis: {
      type: "category",
      data: dates,
      axisLabel: { fontSize: 11, rotate: 30 },
    },
    yAxis: {
      type: "value",
      axisLabel: { fontSize: 11 },
    },
    series: [
      {
        name: "新签约",
        type: "line",
        smooth: true,
        data: rows.map((r) => r["newSign" + suffix]),
        itemStyle: { color: "#1677ff" },
      },
      {
        name: "要扣款",
        type: "line",
        smooth: true,
        data: rows.map((r) => r["toDeduct" + suffix]),
        itemStyle: { color: "#fa8c16" },
      },
      {
        name: "已扣款",
        type: "line",
        smooth: true,
        data: rows.map((r) => r["deducted" + suffix]),
        itemStyle: { color: "#52c41a" },
      },
      {
        name: "首次扣款",
        type: "line",
        smooth: true,
        data: rows.map((r) => r["deductedM0" + suffix]),
        itemStyle: { color: "#722ed1" },
      },
      {
        name: "非首次扣款",
        type: "line",
        smooth: true,
        data: rows.map((r) => r["deductedM1" + suffix]),
        itemStyle: { color: "#ff4d4f" },
      },
      {
        name: "留存签约",
        type: "line",
        smooth: true,
        data: rows.map((r) => r["retained" + suffix]),
        itemStyle: { color: "#13c2c2" },
      },
    ],
  };
});

// ========================
// 表格列定义（按渠道分组）
// ========================

const channelGroup = (title, suffix) => ({
  title,
  key: title,
  customHeaderCell: () => nowrap,
  children: [
    {
      title: "新签约",
      dataIndex: "newSign" + suffix,
      key: "newSign" + suffix,
      width: 90,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => text?.toLocaleString() ?? "-",
    },
    {
      title: "要扣款",
      dataIndex: "toDeduct" + suffix,
      key: "toDeduct" + suffix,
      width: 90,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => text?.toLocaleString() ?? "-",
    },
    {
      title: "已扣款",
      dataIndex: "deducted" + suffix,
      key: "deducted" + suffix,
      width: 90,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => text?.toLocaleString() ?? "-",
    },
    {
      title: "m0已扣",
      dataIndex: "deductedM0" + suffix,
      key: "deductedM0" + suffix,
      width: 90,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => text?.toLocaleString() ?? "-",
    },
    {
      title: "m1已扣",
      dataIndex: "deductedM1" + suffix,
      key: "deductedM1" + suffix,
      width: 90,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => text?.toLocaleString() ?? "-",
    },
    {
      title: "留存签约",
      dataIndex: "retained" + suffix,
      key: "retained" + suffix,
      width: 90,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => text?.toLocaleString() ?? "-",
    },
  ],
});

const columns = [
  {
    title: "统计日期",
    dataIndex: "statDate",
    key: "statDate",
    width: 110,
    fixed: "left",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
  },
  channelGroup("总数", "Total"),
  channelGroup("快钱", "Kq"),
  channelGroup("合利宝", "Hlb"),
  channelGroup("京东", "Jd"),
  {
    title: "更新时间",
    dataIndex: "updatedAt",
    key: "updatedAt",
    width: 165,
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
  },
];

// ========================
// 数据请求
// ========================

const fetchData = async (pageIdx = 1, pageSize = 20) => {
  loading.value = true;
  try {
    const params = {
      pageIdx,
      pageSize,
      startDate: searchForm.startDate || "",
      endDate: searchForm.endDate || "",
    };
    const json = await get(
      analyticsUrl("/order/stats/daily/order-stat"),
      params
    );
    if (json.code === 200 && json.data) {
      const d = json.data;
      tableData.value = d.data;
      pagination.total = d.dataSum;
      pagination.current = d.pageIdx;
      pagination.pageSize = d.pageSize;
    } else {
      message.warning("查询失败：" + (json.msg || "未知错误"));
    }
  } catch (e) {
    message.error("接口请求失败");
  } finally {
    loading.value = false;
  }
};

const handleSearch = () => {
  if (dateRange.value && dateRange.value.length === 2) {
    searchForm.startDate = dateRange.value[0];
    searchForm.endDate = dateRange.value[1];
  } else {
    searchForm.startDate = "";
    searchForm.endDate = "";
  }
  fetchData(1, pagination.pageSize);
};

const handleReset = () => {
  dateRange.value = [];
  searchForm.startDate = "";
  searchForm.endDate = "";
  fetchData(1, pagination.pageSize);
};

const handleTableChange = (pag) => {
  fetchData(pag.current, pag.pageSize);
};

// ========================
// CSV 下载
// ========================

const handleDownload = async () => {
  downloading.value = true;
  try {
    const allData = [];
    let pageIdx = 1;
    let pageCount = 1;
    while (pageIdx <= pageCount) {
      const params = {
        pageIdx,
        pageSize: 100,
        startDate: searchForm.startDate || "",
        endDate: searchForm.endDate || "",
      };
      const json = await get(
        analyticsUrl("/order/stats/daily/order-stat"),
        params
      );
      if (json.code !== 200 || !json.data) break;
      allData.push(...json.data.data);
      pageCount = json.data.pageCount;
      pageIdx++;
    }
    if (!allData.length) {
      message.warning("没有数据可下载");
      return;
    }

    const flatCols = [
      { title: "统计日期", key: "statDate" },
      { title: "新签约-总数", key: "newSignTotal" },
      { title: "新签约-快钱", key: "newSignKq" },
      { title: "新签约-合利宝", key: "newSignHlb" },
      { title: "新签约-京东", key: "newSignJd" },
      { title: "要扣款-总数", key: "toDeductTotal" },
      { title: "要扣款-快钱", key: "toDeductKq" },
      { title: "要扣款-合利宝", key: "toDeductHlb" },
      { title: "要扣款-京东", key: "toDeductJd" },
      { title: "已扣款-总数", key: "deductedTotal" },
      { title: "已扣款-快钱", key: "deductedKq" },
      { title: "已扣款-合利宝", key: "deductedHlb" },
      { title: "已扣款-京东", key: "deductedJd" },
      { title: "m0已扣-总数", key: "deductedM0Total" },
      { title: "m0已扣-快钱", key: "deductedM0Kq" },
      { title: "m0已扣-合利宝", key: "deductedM0Hlb" },
      { title: "m0已扣-京东", key: "deductedM0Jd" },
      { title: "m1已扣-总数", key: "deductedM1Total" },
      { title: "m1已扣-快钱", key: "deductedM1Kq" },
      { title: "m1已扣-合利宝", key: "deductedM1Hlb" },
      { title: "m1已扣-京东", key: "deductedM1Jd" },
      { title: "留存签约-总数", key: "retainedTotal" },
      { title: "留存签约-快钱", key: "retainedKq" },
      { title: "留存签约-合利宝", key: "retainedHlb" },
      { title: "留存签约-京东", key: "retainedJd" },
      { title: "更新时间", key: "updatedAt" },
    ];

    const esc = (v) => {
      const s = String(v ?? "");
      return s.includes(",") || s.includes('"') || s.includes("\n")
        ? '"' + s.replace(/"/g, '""') + '"'
        : s;
    };

    const header = flatCols.map((c) => esc(c.title)).join(",");
    const rows = allData.map((r) =>
      flatCols.map((c) => esc(r[c.key] ?? "")).join(",")
    );
    const csv = "\uFEFF" + [header, ...rows].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `每日订单.csv`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    message.error("下载失败");
  } finally {
    downloading.value = false;
  }
};

onMounted(() => {
  fetchData();
});
</script>
